/* ============================================================================
   Canonical form and fingerprints of a project — shared by the app and the
   server, so both sides compute the same answer from the same data.

   Projects sync record by record: a device sends only the records it changed
   and fetches only the records others changed. To be sure nothing was missed,
   both sides then compare a fingerprint of the WHOLE project. If they differ,
   the device falls back to the complete copy. So the per-record shortcuts can
   never leave a device quietly out of step.

   The canonical form ignores what does not change meaning:
   - key order inside objects
   - the order of records inside a register (sorted by id)
   - missing keys, undefined values and empty lists (the app fills in empty
     lists for registers a project does not have yet)
   ========================================================================== */

/** A register: a non-empty list whose items are all objects with a string id. */
export function isRecordList(v: unknown): v is { id: string }[] {
  return Array.isArray(v) && v.length > 0 && v.every((x) => !!x && typeof x === "object" && typeof (x as { id?: unknown }).id === "string");
}

function keep(x: unknown): boolean {
  return x !== undefined && typeof x !== "function" && !(Array.isArray(x) && x.length === 0);
}

export function canon(v: unknown): string {
  if (v === null || v === undefined) return "null";
  if (Array.isArray(v)) {
    if (isRecordList(v)) {
      const sorted = [...v].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
      return "[" + sorted.map(canon).join(",") + "]";
    }
    return "[" + v.map((x) => (x === undefined || typeof x === "function" ? "null" : canon(x))).join(",") + "]";
  }
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    const keys = Object.keys(o).filter((k) => keep(o[k])).sort();
    return "{" + keys.map((k) => JSON.stringify(k) + ":" + canon(o[k])).join(",") + "}";
  }
  if (typeof v === "number") return isFinite(v) ? JSON.stringify(v) : "null";
  if (typeof v === "boolean" || typeof v === "string") return JSON.stringify(v);
  return "null";
}

/** Fast 64-bit fingerprint (two FNV-1a passes) — for spotting which record
    changed. A collision would only mean one record is not sent; the whole-
    project check below catches that and recovers. */
export function quickHash(s: string): string {
  let h1 = 0x811c9dc5, h2 = 0x01000193 ^ s.length;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193);
    h2 = Math.imul(h2 ^ c, 0x5bd1e995);
    h2 ^= h2 >>> 15;
  }
  return (h1 >>> 0).toString(16).padStart(8, "0") + (h2 >>> 0).toString(16).padStart(8, "0");
}

/** Whole-project fingerprint (SHA-256 of the canonical form). */
export async function projectHash(p: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(canon(p));
  const d = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(d)].slice(0, 16).map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Per-part fingerprints: "s:<key>" for plain fields, "r:<register>:<id>"
    for each record. */
export function partHashes(p: Record<string, unknown>): Map<string, string> {
  const out = new Map<string, string>();
  for (const k of Object.keys(p)) {
    const v = p[k];
    if (!keep(v)) continue;
    if (isRecordList(v)) for (const r of v) out.set(`r:${k}:${r.id}`, quickHash(canon(r)));
    else out.set(`s:${k}`, quickHash(canon(v)));
  }
  return out;
}

export interface ProjectDelta {
  /** changed plain fields, and only the changed records of each register */
  patch: Record<string, unknown>;
  /** plain fields that were removed */
  unset: string[];
  /** how many records and fields the patch carries */
  size: number;
}

/** What changed in `next` since the fingerprints in `base`. */
export function diffProject(next: Record<string, unknown>, base: Map<string, string>): ProjectDelta {
  const patch: Record<string, unknown> = { id: next.id };
  const unset: string[] = [];
  let size = 0;
  const seenScalars = new Set<string>();
  for (const k of Object.keys(next)) {
    const v = next[k];
    if (!keep(v)) continue;
    if (isRecordList(v)) {
      const changed = v.filter((r) => base.get(`r:${k}:${r.id}`) !== quickHash(canon(r)));
      if (changed.length) { patch[k] = changed; size += changed.length; }
    } else {
      seenScalars.add(k);
      if (base.get(`s:${k}`) !== quickHash(canon(v))) { patch[k] = v; if (k !== "id") size++; }
    }
  }
  for (const bk of base.keys()) {
    if (!bk.startsWith("s:")) continue;
    const k = bk.slice(2);
    if (!seenScalars.has(k) && !isRecordList(next[k])) { unset.push(k); size++; }
  }
  return { patch, unset, size };
}
